/** Save-changes confirmation dialog for Tour_Operator.config edits.
 *
 * On open it does three things:
 *
 *   1. Re-fetches the tour operator detail and compares its
 *      `config_last_modified_ts` against the value we saw on load. If the
 *      server has a newer change the dialog blocks the save and surfaces a
 *      "config has been modified" warning.
 *
 *   2. Renders a per-key diff between the originally-loaded config and the
 *      pending edits, grouped into Added / Removed / Changed sections so
 *      the user can sanity-check the payload before persisting.
 *
 *   3. Requires a short free-text description of the change. This is
 *      threaded through to the `Tour_Operator_Config_Change.comment` audit
 *      row by the backend.
 *
 * The actual PATCH is performed by the parent — this component is purely
 * a confirmation surface. Parent passes `onConfirm({comment, ifUnmodifiedSince})`.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as topsApi from '@/api/tourOperators';

interface DiffEntry {
  path: string;
  before: unknown;
  after: unknown;
  op: 'added' | 'removed' | 'changed';
}

/** Diff two arbitrary JSON objects recursively. Returns a flat list of
 *  leaf-level differences keyed by dotted path. Mirrors the backend's
 *  `diff_paths` walk but emits richer entries for UI rendering. */
function diffConfig(before: unknown, after: unknown, prefix = ''): DiffEntry[] {
  const out: DiffEntry[] = [];
  const isObj = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

  if (isObj(before) && isObj(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    [...keys].sort().forEach((k) => {
      const path = prefix ? `${prefix}.${k}` : k;
      const hasBefore = k in before;
      const hasAfter = k in after;
      if (hasBefore && hasAfter) {
        out.push(...diffConfig(before[k], after[k], path));
      } else if (!hasBefore && hasAfter) {
        out.push({ path, before: undefined, after: after[k], op: 'added' });
      } else if (hasBefore && !hasAfter) {
        out.push({ path, before: before[k], after: undefined, op: 'removed' });
      }
    });
    return out;
  }

  // Compare via JSON for arrays + primitives. Cheap enough for config-sized
  // payloads and matches the backend's whole-list-replacement semantics.
  if (JSON.stringify(before) !== JSON.stringify(after)) {
    out.push({ path: prefix || '(root)', before, after, op: 'changed' });
  }
  return out;
}

function previewValue(v: unknown, max = 80): string {
  if (v === undefined) return '—';
  let s: string;
  try {
    s = JSON.stringify(v);
  } catch {
    s = String(v);
  }
  if (s === undefined) return '—';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

interface Props {
  open: boolean;
  tourOperatorId: number;
  /** Config as it was when the user started editing. */
  originalConfig: Record<string, unknown>;
  /** Config the user is about to persist. */
  pendingConfig: Record<string, unknown>;
  /** `config_last_modified_ts` from the detail fetch that produced
   *  `originalConfig`. Echoed back as `if_unmodified_since`. */
  loadedConfigTs: string | null;
  saving: boolean;
  saveError: string | null;
  onConfirm: (args: { comment: string; ifUnmodifiedSince: string | null }) => void;
  onCancel: () => void;
}

export function SaveConfigChangesDialog({
  open,
  tourOperatorId,
  originalConfig,
  pendingConfig,
  loadedConfigTs,
  saving,
  saveError,
  onConfirm,
  onCancel,
}: Props) {
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);

  // Refetch the detail to compare server-side `config_last_modified_ts`
  // against the timestamp we loaded with. Disabled when the dialog is
  // closed so we don't refetch on every parent re-render.
  const detail = useQuery({
    queryKey: ['tourOperators', 'detail', tourOperatorId, 'concurrency-check'],
    queryFn: () => topsApi.retrieve(tourOperatorId),
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 0,
  });

  const serverTs = detail.data?.config_last_modified_ts ?? null;
  const isStale = useMemo(() => {
    if (!serverTs) return false;        // server has no audit history yet
    if (!loadedConfigTs) return true;   // we never saw any timestamp — must refresh
    return new Date(serverTs).getTime() > new Date(loadedConfigTs).getTime();
  }, [serverTs, loadedConfigTs]);

  const diff = useMemo(
    () => diffConfig(originalConfig, pendingConfig),
    [originalConfig, pendingConfig],
  );

  const grouped = useMemo(() => {
    const g = { added: [] as DiffEntry[], removed: [] as DiffEntry[], changed: [] as DiffEntry[] };
    diff.forEach((e) => g[e.op].push(e));
    return g;
  }, [diff]);

  const trimmedComment = comment.trim();
  const isCommentValid = trimmedComment.length >= 3;
  const canSubmit =
    !saving &&
    !detail.isFetching &&
    !isStale &&
    diff.length > 0 &&
    isCommentValid;

  function handleConfirm() {
    setTouched(true);
    if (!canSubmit) return;
    onConfirm({
      comment: trimmedComment,
      ifUnmodifiedSince: loadedConfigTs,
    });
  }

  function handleClose() {
    if (saving) return;
    setComment('');
    setTouched(false);
    onCancel();
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Save configuration changes</DialogTitle>
      <DialogContent dividers>
        {/* Concurrency check status */}
        {detail.isFetching && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Checking for newer changes from other users…
            </Typography>
          </Box>
        )}
        {detail.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not refresh from the server to verify there are no concurrent
            changes. Please retry, or close this dialog and reload the tab.
          </Alert>
        )}
        {!detail.isFetching && !detail.isError && isStale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The configuration has been modified on the server since you loaded
            it{serverTs ? ` (latest change: ${new Date(serverTs).toLocaleString()})` : ''}.
            Saving is disabled to avoid overwriting someone else&apos;s changes —
            close this dialog and refresh the tab to start over.
          </Alert>
        )}
        {!detail.isFetching && !detail.isError && !isStale && diff.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No changes detected — there&apos;s nothing to save.
          </Alert>
        )}

        {/* Diff summary */}
        {diff.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Pending changes — {diff.length} key{diff.length !== 1 ? 's' : ''}
            </Typography>
            {grouped.added.length > 0 && (
              <DiffSection title={`Added (${grouped.added.length})`} color="success" entries={grouped.added} />
            )}
            {grouped.changed.length > 0 && (
              <DiffSection title={`Changed (${grouped.changed.length})`} color="warning" entries={grouped.changed} />
            )}
            {grouped.removed.length > 0 && (
              <DiffSection title={`Removed (${grouped.removed.length})`} color="error" entries={grouped.removed} />
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Comment field */}
        <TextField
          label="Describe this change *"
          fullWidth
          multiline
          rows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => setTouched(true)}
          helperText={
            touched && !isCommentValid
              ? 'Required — at least 3 characters.'
              : 'Stored on the audit row so future operators can understand the intent.'
          }
          error={touched && !isCommentValid}
          inputProps={{ maxLength: 500 }}
          disabled={saving || isStale}
        />

        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canSubmit}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DiffSection({
  title,
  color,
  entries,
}: {
  title: string;
  color: 'success' | 'warning' | 'error';
  entries: DiffEntry[];
}) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color={`${color}.main`} sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Box
        sx={{
          mt: 0.5,
          maxHeight: 220,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          bgcolor: 'background.default',
        }}
      >
        {entries.map((e) => (
          <Box key={e.path} sx={{ mb: 0.5, fontFamily: 'monospace', fontSize: 12 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{e.path}</Box>{' '}
            {e.op === 'changed' && (
              <>
                <Box component="span" sx={{ color: 'error.main' }}>{previewValue(e.before)}</Box>
                {' → '}
                <Box component="span" sx={{ color: 'success.main' }}>{previewValue(e.after)}</Box>
              </>
            )}
            {e.op === 'added' && (
              <Box component="span" sx={{ color: 'success.main' }}>{previewValue(e.after)}</Box>
            )}
            {e.op === 'removed' && (
              <Box component="span" sx={{ color: 'error.main', textDecoration: 'line-through' }}>
                {previewValue(e.before)}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
